import { apiClient } from "../../apiClient";
import {
  ApiResponse,
  ApiClientError,
  PaginatedResponse,
} from "../../../types/api";
import {
  DailyEntry,
  DailyEntryApiResponse,
  CreateDailyEntryRequest,
  UpdateDailyEntryRequest,
  GetDailyEntriesParams,
  GetDailyEntriesResponse,
} from "../../../types/dailyEntry";

interface DailyEntriesApiResponse
  extends PaginatedResponse<DailyEntryApiResponse> {
  filtersApplied: {
    entryDate: string | null;
    fromDate: string | null;
    toDate: string | null;
    customerId: string | null;
    countryId: string | null;
  };
}

class DailyEntryService {
  private async transformDailyEntry(
    apiDailyEntry: DailyEntryApiResponse,
  ): Promise<DailyEntry> {
    return {
      ...apiDailyEntry,
    };
  }

  private handleApiError(error: unknown, defaultMessage: string): never {
    const apiError = error as ApiClientError;
    console.error(`Error: ${defaultMessage}`, apiError);

    const errorMessage =
      apiError.response?.data?.message || apiError.message || defaultMessage;

    throw new Error(errorMessage);
  }

  private buildQueryParams(params: GetDailyEntriesParams): URLSearchParams {
    const queryParams = new URLSearchParams();

    if (params.skip !== undefined)
      queryParams.append("skip", params.skip.toString());
    if (params.limit !== undefined)
      queryParams.append("limit", params.limit.toString());

    if (params.fromDate && params.toDate) {
      queryParams.append("fromDate", params.fromDate);
      queryParams.append("toDate", params.toDate);
    } else if (params.entryDate) {
      queryParams.append("entryDate", params.entryDate);
    }

    if (params.customerId) {
      queryParams.append("customerId", params.customerId);
    }
    if (params.countryId) {
      queryParams.append("countryId", params.countryId);
    }
    if (params.export) {
      queryParams.append("export", params.export.toString());
    }

    return queryParams;
  }

  async getDailyEntries(
    params: GetDailyEntriesParams = {},
  ): Promise<GetDailyEntriesResponse> {
    try {
      const queryParams = this.buildQueryParams(params);

      if (params.export) {
        const response = await apiClient.get<
          ApiResponse<DailyEntryApiResponse[]>
        >(`/daily-entries?${queryParams.toString()}`);

        if (!response.data.success) {
          throw new Error(
            response.data.message || "Failed to export daily entries",
          );
        }

        const entries = response.data.data || [];
        const transformedEntries = await Promise.all(
          entries.map((entry) => this.transformDailyEntry(entry)),
        );

        return {
          dailyEntries: transformedEntries,
          total: transformedEntries.length,
          skip: 0,
          limit: transformedEntries.length,
        };
      } else {
        const response = await apiClient.get<
          ApiResponse<DailyEntriesApiResponse>
        >(`/daily-entries?${queryParams.toString()}`);

        if (!response.data.success) {
          throw new Error(
            response.data.message || "Failed to fetch daily entries",
          );
        }

        const { data } = response.data;
        const entries = data.items || [];
        const { pagination } = data;

        const transformedEntries = await Promise.all(
          entries.map((entry) => this.transformDailyEntry(entry)),
        );

        return {
          dailyEntries: transformedEntries,
          total: pagination?.total || transformedEntries.length,
          skip: pagination?.skip || params.skip || 0,
          limit: pagination?.limit || params.limit || 100,
        };
      }
    } catch (error) {
      this.handleApiError(error, "Failed to fetch daily entries");
    }
  }

  async getDailyEntry(id: string): Promise<DailyEntry> {
    try {
      const response = await apiClient.get<ApiResponse<DailyEntryApiResponse>>(
        `/daily-entries/${id}`,
      );

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to fetch daily entry");
      }

      return this.transformDailyEntry(response.data.data);
    } catch (error) {
      this.handleApiError(error, "Failed to fetch daily entry");
    }
  }

  async createDailyEntry(data: CreateDailyEntryRequest): Promise<DailyEntry> {
    try {
      const response = await apiClient.post<ApiResponse<DailyEntryApiResponse>>(
        "/daily-entries",
        data,
      );

      if (!response.data.success) {
        throw new Error(
          response.data.message || "Failed to create daily entry",
        );
      }

      return this.transformDailyEntry(response.data.data);
    } catch (error) {
      this.handleApiError(error, "Failed to create daily entry");
    }
  }

  async updateDailyEntry(
    entryId: string,
    data: UpdateDailyEntryRequest,
  ): Promise<DailyEntry> {
    try {
      const response = await apiClient.put<ApiResponse<DailyEntryApiResponse>>(
        `/daily-entries/${entryId}`,
        data,
      );

      if (!response.data.success) {
        throw new Error(
          response.data.message || "Failed to update daily entry",
        );
      }

      return this.transformDailyEntry(response.data.data);
    } catch (error) {
      this.handleApiError(error, "Failed to update daily entry");
    }
  }

  async deleteDailyEntry(id: string): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse<null>>(
        `/daily-entries/${id}`,
      );

      if (!response.data.success) {
        throw new Error(
          response.data.message || "Failed to delete daily entry",
        );
      }
    } catch (error) {
      this.handleApiError(error, "Failed to delete daily entry");
    }
  }

  async createCustomerEntry(customerId: string): Promise<DailyEntry> {
    try {
      const response = await apiClient.post<ApiResponse<DailyEntryApiResponse>>(
        "/daily-entries/customer-entry",
        { customer_id: customerId },
      );

      if (!response.data.success) {
        throw new Error(
          response.data.message || "Failed to create customer entry",
        );
      }

      return this.transformDailyEntry(response.data.data);
    } catch (error) {
      this.handleApiError(error, "Failed to create customer entry");
    }
  }
}

export const dailyEntryService = new DailyEntryService();

export type {
  GetDailyEntriesParams,
  GetDailyEntriesResponse,
  CreateDailyEntryRequest,
  UpdateDailyEntryRequest,
};
